import { useCallback, useEffect, useRef, useState } from 'react'
import { PAGE_HEIGHT, PAGE_WIDTH, updateElement } from '../templateDocument'
import TemplateElement from './TemplateElement'

const PIXELS_PER_MM = 96 / 25.4
const clamp = (value, minimum, maximum) =>
  Math.min(maximum, Math.max(minimum, value))
const round = (value) => Math.round(value * 100) / 100

function geometryFor(gesture, clientX, clientY) {
  const dx = (clientX - gesture.clientX) * gesture.mmPerPixel
  const dy = (clientY - gesture.clientY) * gesture.mmPerPixel
  const original = gesture.element
  if (gesture.mode === 'move') {
    return {
      x: round(clamp(original.x + dx, 0, PAGE_WIDTH - original.width)),
      y: round(clamp(original.y + dy, 0, PAGE_HEIGHT - original.height)),
      width: original.width,
      height: original.height,
    }
  }
  let left = original.x
  let top = original.y
  let right = original.x + original.width
  let bottom = original.y + original.height
  const minimumHeight = original.type === 'line' ? 0.25 : 4
  if (gesture.corner.includes('w')) left = clamp(left + dx, 0, right - 4)
  if (gesture.corner.includes('e'))
    right = clamp(right + dx, left + 4, PAGE_WIDTH)
  if (gesture.corner.includes('n'))
    top = clamp(top + dy, 0, bottom - minimumHeight)
  if (gesture.corner.includes('s'))
    bottom = clamp(bottom + dy, top + minimumHeight, PAGE_HEIGHT)
  return {
    x: round(left),
    y: round(top),
    width: round(right - left),
    height: round(bottom - top),
  }
}

export default function TemplateCanvas({
  document: templateDocument,
  onChange,
  selectedId,
  onSelect,
  onPageSelect,
  zoom = 0.8,
  preview = false,
  showGuides = true,
  values = {},
}) {
  const [draft, setDraft] = useState(null)
  const gestureRef = useRef(null)
  const frameRef = useRef(null)
  const documentRef = useRef(templateDocument)
  documentRef.current = templateDocument
  const scale = clamp(
    Number.isFinite(Number(zoom)) ? Number(zoom) : 0.8,
    0.1,
    2,
  )
  const pages = Array.isArray(templateDocument?.pages)
    ? templateDocument.pages
    : []
  const margin = clamp(Number(templateDocument?.marginMm) || 0, 0, 50)

  const releaseGesture = useCallback(() => {
    const gesture = gestureRef.current
    gestureRef.current = null
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current)
      frameRef.current = null
    }
    if (gesture?.target.hasPointerCapture?.(gesture.pointerId)) {
      gesture.target.releasePointerCapture(gesture.pointerId)
    }
    return gesture
  }, [])

  const cancelGesture = useCallback(() => {
    releaseGesture()
    setDraft(null)
  }, [releaseGesture])

  useEffect(() => {
    window.addEventListener('blur', cancelGesture)
    return () => {
      window.removeEventListener('blur', cancelGesture)
      releaseGesture()
    }
  }, [cancelGesture, releaseGesture])

  useEffect(() => {
    if (preview) cancelGesture()
  }, [preview, cancelGesture])

  useEffect(() => {
    if (gestureRef.current && gestureRef.current.document !== templateDocument)
      cancelGesture()
  }, [templateDocument, cancelGesture])

  const startInteraction = useCallback(
    (event, element, mode, corner) => {
      if (preview || event.button !== 0 || event.isPrimary === false) return
      event.stopPropagation()
      event.preventDefault()
      onSelect?.(element.id)
      event.currentTarget.focus?.({ preventScroll: true })
      if (element.locked || gestureRef.current) return
      const page = event.currentTarget.closest('[data-lt-page]')
      const bounds = page?.getBoundingClientRect()
      if (!bounds?.width) return
      gestureRef.current = {
        pointerId: event.pointerId,
        target: event.currentTarget,
        document: documentRef.current,
        element,
        mode,
        corner,
        clientX: event.clientX,
        clientY: event.clientY,
        mmPerPixel: PAGE_WIDTH / bounds.width,
        latest: {
          x: element.x,
          y: element.y,
          width: element.width,
          height: element.height,
        },
      }
      event.currentTarget.setPointerCapture(event.pointerId)
    },
    [onSelect, preview],
  )

  const moveInteraction = (event) => {
    const gesture = gestureRef.current
    if (!gesture || gesture.pointerId !== event.pointerId) return
    event.preventDefault()
    gesture.latest = geometryFor(gesture, event.clientX, event.clientY)
    if (frameRef.current === null) {
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = null
        const current = gestureRef.current
        if (current) setDraft({ id: current.element.id, ...current.latest })
      })
    }
  }

  const finishInteraction = (event) => {
    const gesture = gestureRef.current
    if (!gesture || gesture.pointerId !== event.pointerId) return
    event.preventDefault()
    // The final pointer position also covers a pointerup that arrived before the animation frame.
    gesture.latest = geometryFor(gesture, event.clientX, event.clientY)
    releaseGesture()
    setDraft(null)
    const changed = Object.keys(gesture.latest).some(
      (key) => gesture.latest[key] !== gesture.element[key],
    )
    if (changed) {
      const next = updateElement(
        documentRef.current,
        gesture.element.id,
        gesture.latest,
      )
      if (next !== documentRef.current) onChange?.(next)
    }
  }

  const handleKeyDown = (event) => {
    if (
      preview ||
      event.target.isContentEditable ||
      event.ctrlKey ||
      event.metaKey ||
      event.altKey
    )
      return
    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      if (gestureRef.current) cancelGesture()
      else onSelect?.(null)
      return
    }
    const directions = {
      ArrowLeft: [-1, 0],
      ArrowRight: [1, 0],
      ArrowUp: [0, -1],
      ArrowDown: [0, 1],
    }
    if (!directions[event.key] || gestureRef.current) return
    const element = pages
      .flatMap((page) => page.elements)
      .find((item) => item.id === selectedId)
    if (!element || element.locked) return
    event.preventDefault()
    event.stopPropagation()
    const step = event.shiftKey ? 5 : 1
    const [dx, dy] = directions[event.key].map((value) => value * step)
    const corner = event.target.closest('[data-lt-resize-handle]')?.dataset
      .ltResizeHandle
    const patch = corner
      ? geometryFor(
          {
            element,
            mode: 'resize',
            corner,
            clientX: 0,
            clientY: 0,
            mmPerPixel: 1,
          },
          dx,
          dy,
        )
      : { x: element.x + dx, y: element.y + dy }
    const next = updateElement(templateDocument, element.id, patch)
    if (next !== templateDocument) onChange?.(next)
  }

  return (
    <div
      className={`lt-canvas${preview ? ' lt-canvas-preview' : ''}`}
      data-testid="template-canvas"
      data-preview={preview ? 'true' : 'false'}
      data-zoom={scale}
      role="region"
      aria-label={preview ? 'Prévia do documento' : 'Páginas do documento'}
      onPointerMove={moveInteraction}
      onPointerUp={finishInteraction}
      onPointerCancel={cancelGesture}
      onLostPointerCapture={() => {
        if (gestureRef.current) cancelGesture()
      }}
      onKeyDown={handleKeyDown}
    >
      {pages.map((page, index) => (
        <div className="lt-page-group" key={page.id} data-page-id={page.id}>
          {!preview && (
            <div className="lt-page-label" aria-hidden="true">
              Página {index + 1}
            </div>
          )}
          <div
            className="lt-page-shell"
            style={{
              width: PAGE_WIDTH * PIXELS_PER_MM * scale,
              height: PAGE_HEIGHT * PIXELS_PER_MM * scale,
              position: 'relative',
            }}
          >
            <div
              className="lt-page"
              data-lt-page="true"
              data-page-id={page.id}
              data-page-number={index + 1}
              aria-label={`Página ${index + 1}`}
              onPointerDown={(event) => {
                if (!preview && !event.target.closest('[data-element-id]')) {
                  onSelect?.(null)
                  onPageSelect?.(page.id)
                }
              }}
              style={{
                position: 'relative',
                width: `${PAGE_WIDTH}mm`,
                height: `${PAGE_HEIGHT}mm`,
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
                background: '#ffffff',
                color: '#17212b',
                overflow: 'hidden',
                boxSizing: 'border-box',
              }}
            >
              {showGuides && !preview && (
                <div
                  className="lt-page-guides"
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    inset: `${margin}mm`,
                    pointerEvents: 'none',
                  }}
                />
              )}
              {(page.elements || []).map((element) => (
                <TemplateElement
                  key={element.id}
                  element={
                    draft?.id === element.id
                      ? { ...element, ...draft, id: element.id }
                      : element
                  }
                  selected={selectedId === element.id}
                  preview={preview}
                  values={values}
                  onInteractionStart={startInteraction}
                  onSelect={onSelect}
                />
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}
